import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAdminQuestionnaires,
  setAdminQuestionnaireStatus,
  deleteAdminQuestionnaire,
} from '../../api';
import useNoIndex from '../../hooks/useNoIndex';

export default function QuestionnaireList() {
  useNoIndex();
  const [questionnaires, setQuestionnaires] = useState(null);
  const [error, setError] = useState(null);

  function load() {
    fetchAdminQuestionnaires()
      .then(setQuestionnaires)
      .catch((err) => {
        if (err?.response?.status === 401) setError('Authentication cancelled or invalid.');
        else setError(err?.message || 'Could not load questionnaires.');
      });
  }

  useEffect(load, []);

  async function toggleStatus(q) {
    const next = q.status === 'published' ? 'archived' : 'published';
    try {
      await setAdminQuestionnaireStatus(q.id, next);
      load();
    } catch (err) {
      window.alert(err?.response?.data?.errors?.[0] || 'Could not update status.');
    }
  }

  async function remove(q) {
    if (!window.confirm(`Delete "${q.title}"? This can't be undone.`)) return;
    try {
      await deleteAdminQuestionnaire(q.id);
      load();
    } catch (err) {
      window.alert(err?.response?.data?.errors?.[0] || 'Could not delete questionnaire.');
    }
  }

  return (
    <div className="page page--wide">
      <div className="admin-toolbar">
        <Link to="/admin">← Submissions</Link>
        <span>
          <Link to="/admin/questionnaires/import">
            <button type="button" className="secondary">Import from JSON</button>
          </Link>
          {' '}
          <Link to="/admin/questionnaires/new">
            <button type="button">+ New questionnaire</button>
          </Link>
        </span>
      </div>

      <h1>Questionnaires</h1>
      <p className="muted small">Multiple questionnaires can be published simultaneously, each at its own URL.</p>

      {error && <div className="error">{error}</div>}
      {!questionnaires && !error && <p className="muted">Loading…</p>}
      {questionnaires && questionnaires.length === 0 && <p className="muted">None yet.</p>}

      {questionnaires && questionnaires.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Status</th>
                <th>Max score</th>
                <th>Submissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {questionnaires.map((q) => (
                <tr key={q.id}>
                  <td>{q.title}</td>
                  <td><code>{q.slug}</code></td>
                  <td>
                    <span className={`qb-badge qb-badge--${q.status}`}>{q.status}</span>
                  </td>
                  <td>{q.maxScore}</td>
                  <td>{q.submissionCount}</td>
                  <td>
                    <Link to={`/admin/questionnaires/${q.id}`}>Edit</Link>
                    {' · '}
                    {q.status === 'published' && (
                      <a href={`/scorecard/${q.slug}`} target="_blank" rel="noopener noreferrer">Preview</a>
                    )}
                    {' · '}
                    <button type="button" className="secondary" onClick={() => toggleStatus(q)}>
                      {q.status === 'published' ? 'Archive' : 'Publish'}
                    </button>
                    {q.status === 'archived' && q.submissionCount === 0 && (
                      <>
                        {' '}
                        <button type="button" className="secondary" onClick={() => remove(q)}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
